import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Star, FileText, X, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";

const sentimentColor = {
  positive: "text-emerald-400 bg-emerald-950/60 border-emerald-800/50",
  neutral: "text-sky-300 bg-sky-950/50 border-sky-800/50",
  negative: "text-rose-300 bg-rose-950/50 border-rose-800/50",
};

export default function SummaryModal({ clip, open, onClose, isFavorite, onToggleFavorite, onSendToStudio }) {
  if (!clip) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="claude-full-summary-modal"
        className="bg-[#121721] border-white/10 text-zinc-100 max-w-2xl p-0 overflow-hidden gap-0"
      >
        <DialogTitle className="sr-only">{clip.headline}</DialogTitle>
        <DialogDescription className="sr-only">Full news briefing and sources</DialogDescription>
        <div className="relative pf-card-glow p-6 sm:p-8 border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-zinc-500 hover:text-zinc-200 transition-colors"
            data-testid="summary-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs uppercase font-mono tracking-widest text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/40">
              {clip.topic}
            </span>
            <span className={`text-xs uppercase font-mono tracking-widest px-2 py-0.5 rounded border ${sentimentColor[clip.sentiment] || sentimentColor.neutral}`}>
              {clip.sentiment}
            </span>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl font-bold leading-tight text-zinc-50 pr-8">
            {clip.headline}
          </h2>
        </div>

        <div className="p-6 sm:p-8 max-h-[48vh] overflow-y-auto">
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">The briefing</h3>
          <p className="text-zinc-300 leading-relaxed text-[15px]" data-testid="claude-summary-takeaways-list">
            {clip.summary_full || clip.summary_short}
          </p>

          {clip.sources?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">Sources</h3>
              <div className="flex flex-wrap gap-2">
                {clip.sources.map((s, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-sm font-mono text-sky-300 bg-sky-950/40 border border-sky-900/50 px-2.5 py-1 rounded-full">
                    <ExternalLink className="w-3 h-3" /> {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-white/10 flex items-center gap-3 bg-[#0F141C]">
          <Button
            data-testid="modal-favorite-button"
            onClick={() => onToggleFavorite(clip)}
            variant="outline"
            className={`flex-1 h-11 rounded-xl border-white/10 transition-colors ${
              isFavorite
                ? "bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25"
                : "bg-transparent text-zinc-300 hover:bg-white/5 hover:text-white"
            }`}
          >
            <motion.span whileTap={{ scale: 0.8 }} className="flex items-center gap-2">
              <Star className={`w-4 h-4 ${isFavorite ? "fill-amber-300" : ""}`} />
              {isFavorite ? "Saved" : "Save favorite"}
            </motion.span>
          </Button>
          <Button
            data-testid="modal-send-to-studio-button"
            onClick={() => onSendToStudio(clip)}
            className="flex-1 h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold transition-colors"
          >
            <FileText className="w-4 h-4 mr-2" /> Send to Studio
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
